import { ConfirmDialog } from '@/components/core/common/feedback/ConfirmDialog';
import { FolderFormModal } from './FolderFormModal';
import { MoveToFolderModal } from './MoveToFolderModal';
import {
  EditLessonDetailsModal,
  type EditLessonDetailsPayload,
} from './EditLessonDetailsModal';
import type { VersionSummary } from '@/lib/api-client';

type FolderFormValues = { name: string; tags: string[] };

export interface MyLearningDialogsProps {
  deleteLesson: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
  };
  deleteFolder: {
    isOpen: boolean;
    /** Interpolated into the title. Empty string while closing. */
    folderName: string;
    onClose: () => void;
    onConfirm: () => void;
  };
  createFolder: {
    isOpen: boolean;
    /** Root says "New folder"; inside a folder it is a subfolder. */
    isRoot: boolean;
    onClose: () => void;
    onSubmit: (values: FolderFormValues) => Promise<void> | void;
  };
  renameFolder: {
    isOpen: boolean;
    initial?: FolderFormValues;
    onClose: () => void;
    onSubmit: (values: FolderFormValues) => Promise<void> | void;
  };
  moveLesson: {
    isOpen: boolean;
    currentFolderId: string | null;
    onClose: () => void;
    onSelect: (folderId: string | null) => Promise<void> | void;
  };
  editDetails: {
    isOpen: boolean;
    initialTitle: string;
    initialDescription: string;
    initialTags: string[];
    versions?: VersionSummary[];
    onClose: () => void;
    onSubmit: (payload: EditLessonDetailsPayload) => Promise<void> | void;
  };
}

/**
 * Every overlay the library can open, in one place.
 *
 * All six are mounted unconditionally and gated on their own `isOpen` — which
 * is what lets a close animation run, and why the delete-folder title has to
 * tolerate an empty name for the frame after the target is cleared.
 *
 * Grouped per dialog rather than flattened into 20-odd props so a caller cannot
 * pair one dialog's `isOpen` with another's `onConfirm`, and so a story can
 * open exactly one.
 */
export function MyLearningDialogs({
  deleteLesson,
  deleteFolder,
  createFolder,
  renameFolder,
  moveLesson,
  editDetails,
}: MyLearningDialogsProps) {
  return (
    <>
      <ConfirmDialog
        isOpen={deleteLesson.isOpen}
        onClose={deleteLesson.onClose}
        onConfirm={deleteLesson.onConfirm}
        title='Delete Lesson'
        description='Are you sure you want to delete this lesson? This action cannot be undone and all lesson content will be permanently removed.'
        confirmText='Delete'
        cancelText='Cancel'
        variant='danger'
      />

      <ConfirmDialog
        isOpen={deleteFolder.isOpen}
        onClose={deleteFolder.onClose}
        onConfirm={deleteFolder.onConfirm}
        title={`Delete "${deleteFolder.folderName}"?`}
        description='The folder will be removed. Any lessons and subfolders inside will be moved up one level - they are not deleted.'
        confirmText='Delete folder'
        cancelText='Cancel'
        variant='danger'
      />

      <FolderFormModal
        isOpen={createFolder.isOpen}
        onClose={createFolder.onClose}
        onSubmit={createFolder.onSubmit}
        title={createFolder.isRoot ? 'New folder' : 'New subfolder'}
        submitLabel='Create folder'
      />

      <FolderFormModal
        isOpen={renameFolder.isOpen}
        onClose={renameFolder.onClose}
        onSubmit={renameFolder.onSubmit}
        initial={renameFolder.initial}
        title='Rename folder'
        submitLabel='Save'
      />

      <MoveToFolderModal
        isOpen={moveLesson.isOpen}
        onClose={moveLesson.onClose}
        onSelect={moveLesson.onSelect}
        currentFolderId={moveLesson.currentFolderId}
      />

      <EditLessonDetailsModal
        isOpen={editDetails.isOpen}
        onClose={editDetails.onClose}
        initialTitle={editDetails.initialTitle}
        initialDescription={editDetails.initialDescription}
        initialTags={editDetails.initialTags}
        onSubmit={editDetails.onSubmit}
        versions={editDetails.versions}
      />
    </>
  );
}
